import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
	Box,
	Grid2 as Grid,
	TextField,
	Button,
	Typography,
	FormControlLabel,
	Radio,
	RadioGroup,
	Autocomplete,
	Switch,
	FormControl,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	IconButton,
	Card,
	CardContent,
	Divider,
	Alert,
	Snackbar,
	CircularProgress
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import { useQueryClient } from "@tanstack/react-query";
import {
	useCreateFreelancerProposal,
	useFreelancerProposalByProject
} from "../../../hooks/useFreelancerProposal";
import { useCurrentFreelancer } from "../../../hooks/useFreelancer";
import { useProject } from "../../../hooks/useProject";
import { useCanEditProposal } from "../../../hooks/useProposalWorkflow";
import { useAuthProvider } from "../../../auth/hooks/auth-provider";
import { UserType } from '../../../models/UserType';
import DocumentTable, { DocumentItem } from "../../../components/common/DocumentTable";
import { convertFileToBase64 } from "../../../utils/file";
import { zoullooColors } from "../../../theme";
import HtmlEditor from "../../../components/UI/HtmlEditor";
import {
	useFreelancerProposalDocuments,
	useUploadFreelancerProposalDocument,
	useDeleteFreelancerProposalDocument
} from "../../../hooks/useFreelancerProposalDocuments";
import {
	FreelancerProposalPricingType,
	FreelancerProposalExpenseNature,
	CreateFreelancerProposalRequest,
	CreateFreelancerProposalTaskRequest,
	CreateFreelancerProposalExternalCostRequest,
	CreateFreelancerProposalExpenseRequest
} from "../../../models/FreelancerProposal";
import { FreelancerProposalStatus } from "../../../models/ProposalWorkflow";

interface ProposalFormProps {
	projectId: string;
	onCancel: () => void;
	onSubmit?: () => void;
	onHasChangesUpdate?: (hasChanges: boolean) => void;
}

export interface ProposalFormHandle {
	saveFormChanges: () => Promise<boolean>;
}

interface TaskStep {
	id: string;
	description: string;
	days?: number;
	hours?: number;
	dailyRate?: number;
	hourlyRate?: number;
}

interface ExternalCost {
	id: string;
	description: string;
	amount: number;
}

interface Expense {
	id: string;
	nature: string;
	description: string;
	amount: number;
}

// Helper to create a Date that serializes to YYYY-MM-DD format only
const createDateOnly = (dateString: string): Date => {
	const date = new Date(dateString + "T00:00:00.000Z");
	date.toJSON = () => dateString;
	return date;
};

const ProposalForm = forwardRef<ProposalFormHandle, ProposalFormProps>(
	({ projectId, onCancel, onSubmit, onHasChangesUpdate }, ref) => {
		const { t, i18n } = useTranslation();
		const queryClient = useQueryClient();
		const { user } = useAuthProvider();

		// API hooks
		const createProposalMutation = useCreateFreelancerProposal();
  // Only fetch if user is a freelancer (this component should only be rendered for freelancers)
  const isFreelancer = user.type === UserType.Freelancer;
		const { data: currentFreelancer, isLoading: isLoadingFreelancer } =
			useCurrentFreelancer(isFreelancer);

		// First fetch without polling to get initial proposal state
		const { data: initialProposal, isLoading: isLoadingExistingProposal } =
			useFreelancerProposalByProject(parseInt(projectId), false);

		// Determine if we should enable polling based on proposal status
		// Enable polling ONLY when waiting for EXTERNAL action (CSM/Client to review)
		// NOT when talent needs to act (InDiscussion states = talent must edit)
		const shouldEnablePolling =
			initialProposal &&
			[
				FreelancerProposalStatus.SubmittedCSM, // Waiting for CSM initial review
				FreelancerProposalStatus.SubmittedClient, // Waiting for Client initial review
				FreelancerProposalStatus.RevisedForCSM, // Talent revised, waiting for CSM feedback
				FreelancerProposalStatus.RevisedForClient, // Talent revised, waiting for Client feedback
				// InDiscussionWithCSM/Client NOT included = CSM/Client requested revision, talent must edit now
				FreelancerProposalStatus.CastingToSchedule, // Client requested casting, waiting for CSM to schedule
				FreelancerProposalStatus.CastingScheduled // Casting scheduled, waiting for Client decision
			].includes(initialProposal.status);

		// Second query with conditional polling for submitted proposals
		const { data: polledProposal } = useFreelancerProposalByProject(
			parseInt(projectId),
			shouldEnablePolling || false
		);

		// Use polled data if available (when polling is active), otherwise use initial data
		const existingProposal =
			shouldEnablePolling && polledProposal ? polledProposal : initialProposal;

		const { data: project } = useProject(parseInt(projectId));

		// Check if user can edit the proposal using workflow API
		const { data: canEditFromWorkflow, isLoading: loadingCanEdit } = useCanEditProposal(
			existingProposal?.id,
			!!existingProposal
		);

		// Document hooks
		const { data: proposalDocuments = [], refetch: refetchDocuments } =
			useFreelancerProposalDocuments(existingProposal?.id || 0);
		const uploadDocumentMutation = useUploadFreelancerProposalDocument();
		const deleteDocumentMutation = useDeleteFreelancerProposalDocument();

		// UI State
		const [isSubmitting, setIsSubmitting] = useState(false);
		const [isDraftSaving, setIsDraftSaving] = useState(false);
		const [snackbar, setSnackbar] = useState<{
			open: boolean;
			message: string;
			severity: "success" | "error" | "info";
		}>({
			open: false,
			message: "",
			severity: "success"
		});

		// Form state
		const [relevantExperience, setRelevantExperience] = useState("");
		const [projectStrengths, setProjectStrengths] = useState("");
		const [estimatedStartDate, setEstimatedStartDate] = useState<Dayjs | null>(null);
		const [pricingMode, setPricingMode] = useState<FreelancerProposalPricingType | null>(
			null
		);
		const [comments, setComments] = useState<string | undefined>(undefined);
		const [taskSteps, setTaskSteps] = useState<TaskStep[]>([
			{ id: "1", description: "", days: 0, dailyRate: 0 }
		]);
		const [hasExternalCosts, setHasExternalCosts] = useState(false);
		const [externalCosts, setExternalCosts] = useState<ExternalCost[]>([]);
		const [savedExternalCosts, setSavedExternalCosts] = useState<ExternalCost[]>([]); // Store data when toggle is off
		const [hasExpenses, setHasExpenses] = useState(false);
		const [expenses, setExpenses] = useState<Expense[]>([]);
		const [savedExpenses, setSavedExpenses] = useState<Expense[]>([]); // Store data when toggle is off

		// Accordion states for auto-expansion when toggling
		const [externalCostsExpanded, setExternalCostsExpanded] = useState(false);
		const [expensesExpanded, setExpensesExpanded] = useState(false);

		// Document state
		const [documents, setDocuments] = useState<DocumentItem[]>([]);
		const [isAddingDocument, setIsAddingDocument] = useState(false);
		const [addingDocument, setAddingDocument] = useState<DocumentItem | null>(null);

		// Track initial state for change detection
		const [initialFormSnapshot, setInitialFormSnapshot] = useState<string | null>(null);
		// Track proposal status to detect when it changes (via polling or other means)
		// Initialize with current status to avoid treating first load as a status change
		const [lastProposalStatus, setLastProposalStatus] =
			useState<FreelancerProposalStatus | null>(existingProposal?.status ?? null);

		// Convert proposal documents to DocumentItem format
		React.useEffect(() => {
			if (proposalDocuments && proposalDocuments.length > 0) {
				const convertedDocuments: DocumentItem[] = proposalDocuments.map(doc => ({
					id: doc.id,
					name: doc.fileName,
					urlPath: doc.filePath,
					content: "",
					uploadedAt: doc.uploadedAt,
					uploadedBy: doc.uploadedByUserName || "User"
				}));
				setDocuments(convertedDocuments);
			} else if (documents.length > 0) {
				// Only clear documents if there are currently documents
				// This prevents infinite loop when proposalDocuments is empty
				setDocuments([]);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [proposalDocuments]);

		const expenseNatures = Object.values(FreelancerProposalExpenseNature);

		// Variable pour contrôler l'affichage des sections suivantes
		const showAdvancedSections = pricingMode !== null;

		// Determine if the form should be read-only based on workflow permissions
		// If we have an existing proposal:
		// - If API explicitly says we can edit (true), then it's NOT read-only
		// - If API says we cannot edit (false) or is undefined/loading, then it's read-only for safety
		const isReadOnly: boolean = existingProposal ? canEditFromWorkflow !== true : false;

		// Determine if we're in workflow mode (InDiscussion or Revised states)
		// In these states, form actions are managed by workflow buttons, not form buttons
		const isWorkflowMode =
			existingProposal &&
			[
				FreelancerProposalStatus.InDiscussionWithCSM,
				FreelancerProposalStatus.InDiscussionWithClient,
				FreelancerProposalStatus.RevisedForCSM,
				FreelancerProposalStatus.RevisedForClient
			].includes(existingProposal.status);

		// Check if proposal is submitted (SubmittedCSM or SubmittedClient)
		// In these states, form should be completely disabled except viewing
		const isSubmitted: boolean = !!(
			existingProposal &&
			[
				FreelancerProposalStatus.SubmittedCSM,
				FreelancerProposalStatus.SubmittedClient
			].includes(existingProposal.status)
		);

		// Check if comments field should be disabled (after first submission)
		// The comments field is only editable during initial creation (NotSubmitted status)
		const isCommentsDisabled: boolean = !!(
			existingProposal &&
			existingProposal.status !== FreelancerProposalStatus.NotSubmitted
		);

		// Create a snapshot of current form state for comparison
		const createFormSnapshot = () => {
			return JSON.stringify({
				relevantExperience,
				projectStrengths,
				estimatedStartDate: estimatedStartDate?.format("YYYY-MM-DD"),
				pricingMode,
				comments,
				taskSteps,
				hasExternalCosts,
				externalCosts: hasExternalCosts ? externalCosts : [],
				hasExpenses,
				expenses: hasExpenses ? expenses : []
			});
		};

		// Load existing proposal data when it becomes available
		useEffect(() => {
			if (existingProposal) {
				setRelevantExperience(existingProposal.relevantExperience || "");
				setProjectStrengths(existingProposal.projectStrengths || "");
				setEstimatedStartDate(
					existingProposal.estimatedStartDate
						? dayjs(existingProposal.estimatedStartDate)
						: null
				);
				setPricingMode(existingProposal.pricingMode);

				// Load comments if they exist
				setComments(existingProposal.comments || undefined);

				// Convert tasks from API format to form format
				if (existingProposal.tasks && existingProposal.tasks.length > 0) {
					const convertedTasks: TaskStep[] = existingProposal.tasks.map(
						(task, index) => ({
							id: task.id?.toString() || (index + 1).toString(),
							description: task.description,
							...(existingProposal.pricingMode === FreelancerProposalPricingType.Daily
								? { days: task.quantity, dailyRate: task.unitPrice }
								: { hours: task.quantity, hourlyRate: task.unitPrice })
						})
					);
					setTaskSteps(convertedTasks);
				}

				// Convert external costs
				if (existingProposal.externalCosts && existingProposal.externalCosts.length > 0) {
					setHasExternalCosts(true);
					const convertedCosts: ExternalCost[] = existingProposal.externalCosts.map(
						(cost, index) => ({
							id: cost.id?.toString() || (index + 1).toString(),
							description: cost.description,
							amount: cost.estimatedBudget
						})
					);
					setExternalCosts(convertedCosts);
					setSavedExternalCosts(convertedCosts); // Save the data even if toggle is turned off later
				}

				// Convert expenses
				if (existingProposal.expenses && existingProposal.expenses.length > 0) {
					setHasExpenses(true);
					const convertedExpenses: Expense[] = existingProposal.expenses.map(
						(expense, index) => ({
							id: expense.id?.toString() || (index + 1).toString(),
							nature: expense.nature,
							description: expense.description,
							amount: expense.estimatedBudget
						})
					);
					setExpenses(convertedExpenses);
					setSavedExpenses(convertedExpenses); // Save the data even if toggle is turned off later
				}

				// Convert documents when proposal is loaded
				if (existingProposal.documents && existingProposal.documents.length > 0) {
					const convertedDocuments: DocumentItem[] = existingProposal.documents.map(
						(doc, index) => ({
							id: doc.id || index + 1,
							name: doc.fileName || doc.originalFileName || "Document",
							urlPath: doc.filePath || "",
							content: "", // Will be loaded from server
							uploadedAt: doc.uploadedAt,
							uploadedBy: doc.uploadedByUserName || "User"
						})
					);
					setDocuments(convertedDocuments);
				}
			}
		}, [existingProposal]);

		// Save initial snapshot after data is loaded (with a delay to ensure all state updates are complete)
		useEffect(() => {
			// Create snapshot for BOTH existing proposals and new proposals
			if (!initialFormSnapshot) {
				if (existingProposal) {
					// Wait for all state to be fully loaded from existing proposal
					// Check that critical fields have been populated from existingProposal
					const isDataLoaded =
						relevantExperience !== undefined &&
						pricingMode !== null &&
						taskSteps.length > 0; // Ensure tasks are loaded

					if (isDataLoaded) {
						// Use a longer delay to ensure all async state updates from the previous effect have completed
						const timer = setTimeout(() => {
							const snapshot = createFormSnapshot();
							setInitialFormSnapshot(snapshot);
							// Explicitly notify parent that there are NO changes at initial load
							if (onHasChangesUpdate) {
								onHasChangesUpdate(false);
							}
						}, 300);
						return () => clearTimeout(timer);
					}
				} else {
					// For NEW proposals: create snapshot immediately with empty form state
					const timer = setTimeout(() => {
						const snapshot = createFormSnapshot();
						setInitialFormSnapshot(snapshot);
						// Explicitly notify parent that there are NO changes at initial load
						if (onHasChangesUpdate) {
							onHasChangesUpdate(false);
						}
					}, 300);
					return () => clearTimeout(timer);
				}
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			existingProposal,
			relevantExperience,
			pricingMode,
			taskSteps,
			initialFormSnapshot
		]);

		// Detect changes and notify parent
		useEffect(() => {
			if (initialFormSnapshot && onHasChangesUpdate) {
				const currentSnapshot = createFormSnapshot();
				const hasChanges = currentSnapshot !== initialFormSnapshot;
				onHasChangesUpdate(hasChanges);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			relevantExperience,
			projectStrengths,
			estimatedStartDate,
			pricingMode,
			comments,
			taskSteps,
			hasExternalCosts,
			externalCosts,
			hasExpenses,
			expenses,
			initialFormSnapshot
		]);

		// Check if current status is a revision workflow status (where we need accurate change detection)
		const isRevisionWorkflowStatus = existingProposal?.status && [
			FreelancerProposalStatus.InDiscussionWithCSM,
			FreelancerProposalStatus.InDiscussionWithClient,
			FreelancerProposalStatus.RevisedForCSM,
			FreelancerProposalStatus.RevisedForClient
		].includes(existingProposal.status);

		// Initialize lastProposalStatus when existingProposal first becomes available
		useEffect(() => {
			if (existingProposal && lastProposalStatus === null) {
				setLastProposalStatus(existingProposal.status);

				// If we're loading directly into a revision workflow status,
				// we need to ensure hasUnsavedChanges starts as false
				if ([
					FreelancerProposalStatus.InDiscussionWithCSM,
					FreelancerProposalStatus.InDiscussionWithClient,
					FreelancerProposalStatus.RevisedForCSM,
					FreelancerProposalStatus.RevisedForClient
				].includes(existingProposal.status)) {
					if (onHasChangesUpdate) {
						onHasChangesUpdate(false);
					}
				}
			}
		}, [existingProposal, lastProposalStatus, onHasChangesUpdate]);

		// Force snapshot recreation when entering revision workflow status
		// This ensures we have a fresh baseline for change detection
		useEffect(() => {
			if (existingProposal && isRevisionWorkflowStatus && initialFormSnapshot) {
				// We're in a revision workflow with an existing snapshot
				// Recreate the snapshot to ensure it matches current form state
				const timer = setTimeout(() => {
					const newSnapshot = createFormSnapshot();

					// Only update if snapshots are different (meaning data has been loaded)
					if (newSnapshot !== initialFormSnapshot) {
						setInitialFormSnapshot(newSnapshot);
					}

					// Always ensure hasUnsavedChanges is false when we're in revision workflow
					// and haven't made any changes yet
					if (onHasChangesUpdate) {
						onHasChangesUpdate(false);
					}
				}, 600); // Slightly longer than the initial snapshot delay

				return () => clearTimeout(timer);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [existingProposal?.status, isRevisionWorkflowStatus]);

		// Detect when proposal status changes (via polling) and reset snapshot
		useEffect(() => {
			// Only detect REAL status changes, not initial load
			if (existingProposal && lastProposalStatus !== null && existingProposal.status !== lastProposalStatus) {
				// Status changed - update tracking
				setLastProposalStatus(existingProposal.status);

				// IMMEDIATELY notify parent that there are no changes
				// This prevents the UI from showing "send with changes" while we wait for snapshot recreation
				if (onHasChangesUpdate) {
					onHasChangesUpdate(false);
				}

				// Wait for form data to be fully loaded from the new proposal state
				// The first useEffect (load existing proposal) will have populated the form
				// We need to wait for those state updates to complete before creating a new snapshot
				const timer = setTimeout(() => {
					const newSnapshot = createFormSnapshot();
					setInitialFormSnapshot(newSnapshot);
					// Confirm no changes after snapshot is created
					if (onHasChangesUpdate) {
						onHasChangesUpdate(false);
					}
				}, 500);

				return () => clearTimeout(timer);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [existingProposal?.status, lastProposalStatus]);

		const addTaskStep = () => {
			if (!pricingMode) return;

			const newStep: TaskStep = {
				id: `new_${Date.now()}`,
				description: "",
				...(pricingMode === FreelancerProposalPricingType.Daily
					? { days: 0, dailyRate: 0 }
					: { hours: 0, hourlyRate: 0 })
			};
			setTaskSteps([...taskSteps, newStep]);
		};

		const removeTaskStep = (id: string) => {
			setTaskSteps(taskSteps.filter(step => step.id !== id));
		};

		const updateTaskStep = (id: string, field: keyof TaskStep, value: any) => {
			setTaskSteps(
				taskSteps.map(step => (step.id === id ? { ...step, [field]: value } : step))
			);
		};

		const addExternalCost = () => {
			const newCost: ExternalCost = {
				id: `new_${Date.now()}`,
				description: "",
				amount: 0
			};
			setExternalCosts([...externalCosts, newCost]);
		};

		const removeExternalCost = (id: string) => {
			setExternalCosts(externalCosts.filter(cost => cost.id !== id));
		};

		const updateExternalCost = (id: string, field: keyof ExternalCost, value: any) => {
			setExternalCosts(
				externalCosts.map(cost => (cost.id === id ? { ...cost, [field]: value } : cost))
			);
		};

		const addExpense = () => {
			const newExpense: Expense = {
				id: `new_${Date.now()}`,
				nature: expenseNatures[0],
				description: "",
				amount: 0
			};
			setExpenses([...expenses, newExpense]);
		};

		const removeExpense = (id: string) => {
			setExpenses(expenses.filter(expense => expense.id !== id));
		};

		const updateExpense = (id: string, field: keyof Expense, value: any) => {
			setExpenses(
				expenses.map(expense =>
					expense.id === id ? { ...expense, [field]: value } : expense
				)
			);
		};

		// Document handlers - now connected to real backend APIs
		const handleAddNewDocument = () => {
			setIsAddingDocument(true);
			setAddingDocument({
				name: "",
				urlPath: "",
				content: "",
				type: null
			});
		};

		const handleFileUpload = async (file: File) => {
			try {
				if (!file) return;

				// Validate file size (10MB max)
				const maxSize = 10 * 1024 * 1024;
				if (file.size > maxSize) {
					showSnackbar("File size exceeds 10MB limit", "error");
					return;
				}

				const base64Content = await convertFileToBase64(file);
				setAddingDocument(prev => ({
					...prev!,
					name: file.name,
					urlPath: URL.createObjectURL(file),
					content: base64Content
				}));
			} catch (error) {
				console.error("Error converting file to base64:", error);
				showSnackbar("Error processing file", "error");
			}
		};

		const saveDraftProposal = async () => {
			if (isLoadingFreelancer || !currentFreelancer?.profile?.id) {
				return null;
			}

			try {
				// Create minimal draft proposal with current form values (can be empty for draft)
				const draftProposalData: CreateFreelancerProposalRequest = {
					projectId: parseInt(projectId),
					freelancerProfileId: currentFreelancer.profile.id,
					status: FreelancerProposalStatus.NotSubmitted,
					relevantExperience: relevantExperience.trim() || "",
					projectStrengths: projectStrengths.trim() || "",
					comments: (comments || "").trim(), // Preserve comments field
					estimatedStartDate: estimatedStartDate
						? createDateOnly(estimatedStartDate.format("YYYY-MM-DD"))
						: createDateOnly(dayjs().format("YYYY-MM-DD")),
					pricingMode: pricingMode || FreelancerProposalPricingType.Daily,
					tasks: taskSteps.map((step, index): CreateFreelancerProposalTaskRequest => {
						const mode = pricingMode || FreelancerProposalPricingType.Daily;
						return {
							...(step.id &&
							!step.id.startsWith("temp_") &&
							!step.id.startsWith("new_") &&
							!isNaN(parseInt(step.id))
								? { id: parseInt(step.id) }
								: {}),
							description: step.description || "",
							quantity:
								mode === FreelancerProposalPricingType.Daily
									? step.days ?? 0
									: step.hours ?? 0,
							unitPrice:
								mode === FreelancerProposalPricingType.Daily
									? step.dailyRate ?? 0
									: step.hourlyRate ?? 0,
							orderIndex: index
						};
					}),
					externalCosts: hasExternalCosts
						? externalCosts.map(
								(cost): CreateFreelancerProposalExternalCostRequest => ({
									...(cost.id &&
									!cost.id.startsWith("temp_") &&
									!cost.id.startsWith("new_") &&
									!isNaN(parseInt(cost.id))
										? { id: parseInt(cost.id) }
										: {}),
									description: cost.description || "",
									estimatedBudget: cost.amount || 0
								})
						  )
						: undefined,
					expenses: hasExpenses
						? expenses.map(
								(expense): CreateFreelancerProposalExpenseRequest => ({
									...(expense.id &&
									!expense.id.startsWith("temp_") &&
									!expense.id.startsWith("new_") &&
									!isNaN(parseInt(expense.id))
										? { id: parseInt(expense.id) }
										: {}),
									nature: expense.nature as FreelancerProposalExpenseNature,
									description: expense.description || "",
									estimatedBudget: expense.amount || 0
								})
						  )
						: undefined,
					hasExternalCosts: hasExternalCosts,
					hasExpenses: hasExpenses
				};

				const result = await createProposalMutation.mutateAsync(draftProposalData);

				return result;
			} catch (error: any) {
				console.error("Error creating draft proposal:", error);
				return null;
			}
		};

		const handleConfirmAddDocument = async () => {
			if (!addingDocument?.name || !addingDocument?.content) {
				showSnackbar("Document data is incomplete", "error");
				return;
			}

			try {
				// If no proposal exists yet, create a draft proposal first
				let proposalId = existingProposal?.id;

				if (!proposalId) {
					const draftProposal = await saveDraftProposal();
					if (!draftProposal) {
						showSnackbar("Error creating proposal draft", "error");
						return;
					}
					proposalId = draftProposal.id;
				}

				await uploadDocumentMutation.mutateAsync({
					proposalId: proposalId,
					document: {
						name: addingDocument.name,
						content: addingDocument.content
					}
				});

				setAddingDocument(null);
				setIsAddingDocument(false);
				await refetchDocuments(); // Refresh the documents list
			} catch (error) {
				console.error("Error uploading document:", error);
				// Error handling is done in the mutation hook
			}
		};

		const handleCancelAddDocument = () => {
			setAddingDocument(null);
			setIsAddingDocument(false);
		};

		const handleDocumentChange = (documentChanges: Partial<DocumentItem>) => {
			setAddingDocument(prev => (prev ? { ...prev, ...documentChanges } : null));
		};

		const handleRemoveDocument = async (index: number) => {
			const document = documents[index];
			if (!document?.id || !existingProposal?.id) {
				showSnackbar("Document or proposal not found", "error");
				return;
			}

			try {
				await deleteDocumentMutation.mutateAsync({
					proposalId: existingProposal.id,
					documentId: document.id
				});
				await refetchDocuments(); // Refresh the documents list
			} catch (error) {
				console.error("Error deleting document:", error);
				// Error handling is done in the mutation hook
			}
		};

		const getDocumentUrl = (document: DocumentItem): string => {
			// Generate proper Azure Blob Storage URL for proposal documents
			if (document.urlPath.startsWith("http")) {
				return document.urlPath;
			}

			const storageBaseUrl = process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL;
			const containerName =
				process.env.REACT_APP_FREELANCER_PROPOSAL_DOCUMENTS_CONTAINER_NAME ||
				"freelancer-proposal-documents";

			return `${storageBaseUrl}/${containerName}/${document.urlPath}`;
		};

		const showSnackbar = (message: string, severity: "success" | "error" | "info") => {
			setSnackbar({ open: true, message, severity });
		};

		const handleCloseSnackbar = () => {
			setSnackbar(prev => ({ ...prev, open: false }));
		};

		// Budget calculation functions
		const calculateTasksTotal = (): number => {
			if (!pricingMode) return 0;

			return taskSteps.reduce((total, step) => {
				const quantity =
					pricingMode === FreelancerProposalPricingType.Daily
						? step.days || 0
						: step.hours || 0;
				const unitPrice =
					pricingMode === FreelancerProposalPricingType.Daily
						? step.dailyRate || 0
						: step.hourlyRate || 0;
				return total + quantity * unitPrice;
			}, 0);
		};

		const calculateTotalWithFees = (): number => {
			const tasksTotal = calculateTasksTotal();
			const feePercentage = project?.feePercentage || 0;

			// Protection contre la division par zéro ou valeurs invalides
			if (feePercentage >= 100 || feePercentage < 0) {
				return tasksTotal;
			}

			// Formule : Prix total = TJM / (1 - taux_marge)
			return tasksTotal / (1 - feePercentage / 100);
		};

		const calculateZoullooFees = (): number => {
			// Marge = Prix total - TJM
			return calculateTotalWithFees() - calculateTasksTotal();
		};

		const calculateExternalCostsTotal = (): number => {
			return externalCosts.reduce((total, cost) => total + cost.amount, 0);
		};

		const calculateExternalCostsWithFees = (): number => {
			const externalCostsTotal = calculateExternalCostsTotal();
			const feePercentage = project?.externalCostsFeePercentage || 0;
			return externalCostsTotal * (1 + feePercentage / 100);
		};

		const calculateExpensesTotal = (): number => {
			return expenses.reduce((total, expense) => total + expense.amount, 0);
		};

		const calculateExpensesWithFees = (): number => {
			const expensesTotal = calculateExpensesTotal();
			const feePercentage = project?.expensesFeePercentage || 0;
			return expensesTotal * (1 + feePercentage / 100);
		};

		const calculateGrandTotal = (): number => {
			return (
				calculateTotalWithFees() +
				calculateExternalCostsWithFees() +
				calculateExpensesWithFees() +
				(project?.processingFees || 0)
			);
		};

		// Helper function to check if HTML content is empty (only tags, no text)
		const isHtmlContentEmpty = (html: string): boolean => {
			if (!html) return true;
			// Remove all HTML tags and check if there's any text content
			const textContent = html.replace(/<[^>]*>/g, "").trim();
			return textContent.length === 0;
		};

		const validateRequiredFields = (): string[] => {
			const errors: string[] = [];

			if (isHtmlContentEmpty(relevantExperience)) {
				errors.push(t("project.proposal.validation.experienceRequired"));
			}

			if (isHtmlContentEmpty(projectStrengths)) {
				errors.push(t("project.proposal.validation.strengthsRequired"));
			}

			// Date must be today or in the future (matching backend validation)
			if (estimatedStartDate) {
				const today = new Date();
				today.setHours(0, 0, 0, 0); // Reset to midnight for comparison
				const selectedDate = estimatedStartDate.toDate();
				selectedDate.setHours(0, 0, 0, 0);

				if (selectedDate < today) {
					errors.push(t("project.proposal.validation.startDateFuture"));
				}
			}

			return errors;
		};

		const validateTask = (
			step: any,
			index: number
		): { errors: string[]; isComplete: boolean } => {
			const errors: string[] = [];
			if (!pricingMode)
				return { errors: ["Pricing mode not selected"], isComplete: false };

			const quantity =
				pricingMode === FreelancerProposalPricingType.Daily
					? step.days || 0
					: step.hours || 0;
			const unitPrice =
				pricingMode === FreelancerProposalPricingType.Daily
					? step.dailyRate || 0
					: step.hourlyRate || 0;

			if (step.description && step.description.length > 1000) {
				errors.push(
					t("project.proposal.validation.taskDescriptionLength", { index: index + 1 })
				);
			}

			const isComplete = !!(step.description?.trim() && quantity > 0 && unitPrice > 0);

			if (step.description?.trim()) {
				if (quantity <= 0) {
					const quantityType =
						pricingMode === FreelancerProposalPricingType.Daily
							? t("project.proposal.validation.days")
							: t("project.proposal.validation.hours");
					errors.push(
						t("project.proposal.validation.taskQuantityRequired", {
							index: index + 1,
							type: quantityType
						})
					);
				}
				if (unitPrice <= 0) {
					const priceType =
						pricingMode === FreelancerProposalPricingType.Daily
							? t("project.proposal.validation.dailyRate")
							: t("project.proposal.validation.hourlyRate");
					errors.push(
						t("project.proposal.validation.taskPriceRequired", {
							index: index + 1,
							type: priceType
						})
					);
				}
			}

			if (quantity < 0) {
				errors.push(
					t("project.proposal.validation.taskQuantityNegative", { index: index + 1 })
				);
			}

			if (unitPrice < 0) {
				errors.push(
					t("project.proposal.validation.taskPriceNegative", { index: index + 1 })
				);
			}

			return { errors, isComplete };
		};

		const validateTasks = (): string[] => {
			const errors: string[] = [];

			if (taskSteps.length === 0) {
				errors.push(t("project.proposal.validation.taskRequired"));
				return errors;
			}

			let hasCompleteTask = false;
			taskSteps.forEach((step, index) => {
				const validation = validateTask(step, index);
				errors.push(...validation.errors);
				if (validation.isComplete) {
					hasCompleteTask = true;
				}
			});

			if (!hasCompleteTask) {
				errors.push(t("project.proposal.validation.taskCompleteRequired"));
			}

			return errors;
		};

		const validateExternalCosts = (): string[] => {
			const errors: string[] = [];

			if (hasExternalCosts && externalCosts.length === 0) {
				errors.push(t("project.proposal.validation.externalCostsRequired"));
				return errors;
			}

			if (hasExternalCosts) {
				externalCosts.forEach((cost, index) => {
					if (!cost.description.trim()) {
						errors.push(
							t("project.proposal.validation.externalCostDescriptionRequired", {
								index: index + 1
							})
						);
					}
					if (cost.description.length > 500) {
						errors.push(
							t("project.proposal.validation.externalCostDescriptionLength", {
								index: index + 1
							})
						);
					}
					if (cost.amount <= 0) {
						errors.push(
							t("project.proposal.validation.externalCostAmountRequired", {
								index: index + 1
							})
						);
					}
				});
			}

			return errors;
		};

		const validateExpenses = (): string[] => {
			const errors: string[] = [];

			if (hasExpenses && expenses.length === 0) {
				errors.push(t("project.proposal.validation.expensesRequired"));
				return errors;
			}

			if (hasExpenses) {
				expenses.forEach((expense, index) => {
					if (!expense.nature) {
						errors.push(
							t("project.proposal.validation.expenseNatureRequired", { index: index + 1 })
						);
					}
					if (!expense.description.trim()) {
						errors.push(
							t("project.proposal.validation.expenseDescriptionRequired", {
								index: index + 1
							})
						);
					}
					if (expense.description.length > 500) {
						errors.push(
							t("project.proposal.validation.expenseDescriptionLength", {
								index: index + 1
							})
						);
					}
					if (expense.amount <= 0) {
						errors.push(
							t("project.proposal.validation.expenseAmountRequired", { index: index + 1 })
						);
					}
				});
			}

			return errors;
		};

		const validateForSubmission = (): string[] => {
			return [
				...validateRequiredFields(),
				...validateTasks(),
				...validateExternalCosts(),
				...validateExpenses()
			];
		};

		const mapTaskToRequest = (
			step: any,
			index: number
		): CreateFreelancerProposalTaskRequest => {
			const hasValidId =
				step.id &&
				!step.id.startsWith("temp_") &&
				!step.id.startsWith("new_") &&
				!isNaN(parseInt(step.id));
			const mode = pricingMode || FreelancerProposalPricingType.Daily;

			// Extract quantity based on pricing mode
			let quantity = 0;
			if (mode === FreelancerProposalPricingType.Daily) {
				quantity = step.days !== undefined && step.days !== null ? Number(step.days) : 0;
			} else {
				quantity =
					step.hours !== undefined && step.hours !== null ? Number(step.hours) : 0;
			}

			// Extract unit price based on pricing mode
			let unitPrice = 0;
			if (mode === FreelancerProposalPricingType.Daily) {
				unitPrice =
					step.dailyRate !== undefined && step.dailyRate !== null
						? Number(step.dailyRate)
						: 0;
			} else {
				unitPrice =
					step.hourlyRate !== undefined && step.hourlyRate !== null
						? Number(step.hourlyRate)
						: 0;
			}

			return {
				...(hasValidId ? { id: parseInt(step.id) } : {}),
				description: step.description,
				quantity,
				unitPrice,
				orderIndex: index
			};
		};

		const mapExternalCostToRequest = (
			cost: any
		): CreateFreelancerProposalExternalCostRequest => {
			const hasValidId =
				cost.id &&
				!cost.id.startsWith("temp_") &&
				!cost.id.startsWith("new_") &&
				!isNaN(parseInt(cost.id));

			return {
				...(hasValidId ? { id: parseInt(cost.id) } : {}),
				description: cost.description,
				estimatedBudget: cost.amount
			};
		};

		const mapExpenseToRequest = (
			expense: any
		): CreateFreelancerProposalExpenseRequest => {
			const hasValidId =
				expense.id &&
				!expense.id.startsWith("temp_") &&
				!expense.id.startsWith("new_") &&
				!isNaN(parseInt(expense.id));

			return {
				...(hasValidId ? { id: parseInt(expense.id) } : {}),
				nature: expense.nature as FreelancerProposalExpenseNature,
				description: expense.description,
				estimatedBudget: expense.amount
			};
		};

		const prepareProposalData = (
			status: FreelancerProposalStatus
		): CreateFreelancerProposalRequest => {
			if (!currentFreelancer?.profile?.id) {
				throw new Error("FreelancerProfile ID is required");
			}

			return {
				projectId: parseInt(projectId),
				freelancerProfileId: currentFreelancer.profile.id,
				status,
				relevantExperience: relevantExperience || "",
				projectStrengths: projectStrengths || "",
				estimatedStartDate: estimatedStartDate?.isValid()
					? createDateOnly(estimatedStartDate.format("YYYY-MM-DD"))
					: createDateOnly(dayjs().format("YYYY-MM-DD")),
				comments: comments || "",
				pricingMode: pricingMode || FreelancerProposalPricingType.Daily,
				tasks: taskSteps.map(mapTaskToRequest),
				externalCosts:
					externalCosts.length > 0
						? externalCosts.map(mapExternalCostToRequest)
						: undefined,
				expenses: expenses.length > 0 ? expenses.map(mapExpenseToRequest) : undefined,
				hasExternalCosts: hasExternalCosts,
				hasExpenses: hasExpenses
			};
		};

		const handleSubmitProposal = async () => {

			if (isLoadingFreelancer || !currentFreelancer?.profile?.id) {
				showSnackbar(t("project.proposal.messages.loadingProfile"), "info");
				return;
			}

			// Validation avant soumission
			const validationErrors = validateForSubmission();
			if (validationErrors.length > 0) {
				showSnackbar(
					t("project.proposal.messages.validationErrors", {
						errors: validationErrors.join("\n• ")
					}),
					"error"
				);
				return;
			}

			setIsSubmitting(true);
			try {
				// Submit as final proposal (upsert behavior)
				const proposalData = prepareProposalData(FreelancerProposalStatus.SubmittedCSM);

				const result = await createProposalMutation.mutateAsync(proposalData);

				// Invalidate relevant queries to refresh permissions and proposal status
				await queryClient.invalidateQueries({ queryKey: ["freelancer-proposal"] });
				await queryClient.invalidateQueries({
					queryKey: ["proposal-can-edit", result.id]
				});
				await queryClient.invalidateQueries({
					queryKey: ["project-freelancer-proposal"]
				});

      // Reset change tracking after successful submission
      const newSnapshot = createFormSnapshot();
      setInitialFormSnapshot(newSnapshot);
      if (onHasChangesUpdate) {
        onHasChangesUpdate(false);
      }

				const isUpdate =
					existingProposal?.status === FreelancerProposalStatus.SubmittedCSM;
				showSnackbar(
					isUpdate
						? t("project.proposal.messages.proposalUpdated")
						: t("project.proposal.messages.proposalSubmitted"),
					"success"
				);

				// Call parent onSubmit if provided
				if (onSubmit) {
					onSubmit();
				}
			} catch (error: any) {
				console.error("Error submitting proposal:", error);
				showSnackbar(
					error.response?.data?.message || t("project.proposal.messages.submitError"),
					"error"
				);
			} finally {
				setIsSubmitting(false);
			}
		};

		// Silent save function for workflow actions (returns success boolean)
		const saveFormChanges = async (): Promise<boolean> => {

			if (isLoadingFreelancer || !currentFreelancer?.profile?.id) {
				console.warn("Cannot save: freelancer profile not loaded");
				return false;
			}

			const targetStatus =
				existingProposal?.status || FreelancerProposalStatus.NotSubmitted;
			const isDraft = targetStatus === FreelancerProposalStatus.NotSubmitted;

			// Only validate if not a draft - drafts can be saved with incomplete data
			if (!isDraft) {
				const validationErrors = validateForSubmission();
				if (validationErrors.length > 0) {
					console.error("❌ Validation failed before workflow action:", validationErrors);
					showSnackbar(
						t("project.proposal.messages.validationErrors", {
							errors: validationErrors.join("\n• ")
						}),
						"error"
					);
					return false;
				}
			}

			try {
				const proposalData = prepareProposalData(targetStatus);

				const result = await createProposalMutation.mutateAsync(proposalData);

				// Reset change tracking BEFORE invalidating queries
				// This ensures hasUnsavedChanges is false before any refetch triggers
				const newSnapshot = createFormSnapshot();
				setInitialFormSnapshot(newSnapshot);
				if (onHasChangesUpdate) {
					onHasChangesUpdate(false);
				}

				// Invalidate queries (may trigger refetch but snapshot is already updated)
				await queryClient.invalidateQueries({
					queryKey: ["freelancer-proposal"],
					refetchType: "all"
				});
				await queryClient.invalidateQueries({
					queryKey: ["proposal-can-edit", result.id],
					refetchType: "all"
				});
				await queryClient.invalidateQueries({
					queryKey: ["project-freelancer-proposal"],
					refetchType: "all"
				});
				await queryClient.invalidateQueries({
					queryKey: ["proposal-available-actions", result.id],
					refetchType: "all"
				});

				return true;
			} catch (error: any) {
				console.error("Error saving form changes:", error);
				return false;
			}
		};

		// Expose saveFormChanges to parent via ref
		useImperativeHandle(ref, () => ({
			saveFormChanges
		}));

		const handleSaveDraft = async () => {

			if (isLoadingFreelancer || !currentFreelancer?.profile?.id) {
				showSnackbar(t("project.proposal.messages.loadingProfile"), "info");
				return;
			}

			setIsDraftSaving(true);
			try {
				const success = await saveFormChanges();
				if (success) {
					showSnackbar(
						isWorkflowMode
							? t(
									"project.proposal.messages.changesSaved",
									"Modifications enregistrées avec succès"
							  )
							: t("project.proposal.messages.draftSaved"),
						"success"
					);
				} else {
					showSnackbar("Erreur lors de l'enregistrement du brouillon.", "error");
				}
			} catch (error: any) {
				console.error("Error saving draft:", error);
				showSnackbar(
					error.response?.data?.message ||
						"Erreur lors de l'enregistrement du brouillon.",
					"error"
				);
			} finally {
				setIsDraftSaving(false);
			}
		};

		// Show loading state while fetching existing proposal or permissions
		if (
			isLoadingExistingProposal ||
			isLoadingFreelancer ||
			(existingProposal && loadingCanEdit)
		) {
			return (
				<Box
					sx={{
						p: 3,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minHeight: 200
					}}
				>
					<CircularProgress />
					<Typography sx={{ ml: 2 }}>
						{t("project.proposal.messages.loadingProposal")}
					</Typography>
				</Box>
			);
		}

		return (
			<Box sx={{ p: 3 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 3
					}}
				>
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{existingProposal
							? t("project.proposal.form.modify")
							: t("project.proposal.form.create")}
					</Typography>

					{/* Proposal Status Badge */}
					<Box
						sx={{
							backgroundColor:
								existingProposal?.status &&
								existingProposal.status !== FreelancerProposalStatus.NotSubmitted
									? zoullooColors.success.main
									: zoullooColors.secondary.main,
							color: "white",
							px: 2,
							py: 1,
							borderRadius: "20px",
							fontSize: "0.875rem",
							fontWeight: 500
						}}
					>
						{existingProposal?.status
							? t(`proposal.status.${existingProposal.status}`)
							: t("project.proposal.form.notSubmitted")}
					</Box>
				</Box>

				{/* Alert if proposal is read-only (but not in workflow mode where sidebar shows actions) */}
				{isReadOnly && existingProposal && !isWorkflowMode && (
					<Alert severity="info" sx={{ mb: 3 }}>
						<Typography variant="body2">
							{t(
								"project.proposal.readOnlyMessage",
								"Cette proposition a été soumise et ne peut plus être modifiée directement. Utilisez les actions du workflow si disponibles."
							)}
						</Typography>
					</Alert>
				)}

				<Grid container spacing={3}>
					{/* Expérience pertinente */}
					<Grid size={12}>
						<Box sx={{ mb: 3 }}>
							<Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
								{t("project.proposal.form.relevantExperienceLabel")}
							</Typography>
							<HtmlEditor
								content={relevantExperience}
								onUpdate={({ editor }) => {
									const newValue = editor.getHTML();
									setRelevantExperience(newValue);
								}}
								placeholder={t("project.proposal.form.relevantExperiencePlaceholder")}
								readonly={isReadOnly || isSubmitted}
								sx={{
									"& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror":
										{
											height: "150px !important"
										}
								}}
							/>
						</Box>
					</Grid>

					{/* Points forts du projet */}
					<Grid size={12}>
						<Box sx={{ mb: 3 }}>
							<Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
								{t("project.proposal.form.projectStrengthsLabel")}
							</Typography>
							<HtmlEditor
								content={projectStrengths}
								onUpdate={({ editor }) => {
									const newValue = editor.getHTML();
									setProjectStrengths(newValue);
								}}
								placeholder={t("project.proposal.form.projectStrengthsPlaceholder")}
								readonly={isReadOnly || isSubmitted}
								sx={{
									"& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror":
										{
											height: "150px !important"
										}
								}}
							/>
						</Box>
					</Grid>

					<Grid size={{ xs: 12, md: 4 }}>
						<Box sx={{ mb: 3 }}>
							<Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
								{t("project.proposal.form.estimatedStartDate")}
							</Typography>
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale={i18n.language === "fr" ? "fr" : "en"}
							>
								<DatePicker
									value={estimatedStartDate}
									onChange={newValue => {
										if (newValue === null || newValue?.isValid()) {
											setEstimatedStartDate(newValue);
										}
									}}
									disabled={isReadOnly || isSubmitted}
									slotProps={{
										textField: {
											fullWidth: true,
											placeholder: t(
												"project.proposal.form.estimatedStartDatePlaceholder"
											),
											disabled: isReadOnly
										}
									}}
								/>
							</LocalizationProvider>
						</Box>
					</Grid>

					<Grid size={{ xs: 12, md: 8 }}>
						<Box sx={{ mb: 3 }}>
							<Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
								{t("project.proposal.form.pricingMode")}
							</Typography>
							<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
								<Typography variant="body2" sx={{ color: "text.secondary" }}>
									{t("project.proposal.form.pricingModeQuestion")}
								</Typography>
								<FormControl component="fieldset">
									<RadioGroup
										row
										value={pricingMode}
										onChange={e =>
											!isReadOnly &&
											setPricingMode(e.target.value as FreelancerProposalPricingType)
										}
									>
										<FormControlLabel
											value={FreelancerProposalPricingType.Daily}
											control={
												<Radio size="small" disabled={isReadOnly || isSubmitted} />
											}
											label={t("project.proposal.form.dailyMode")}
											sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
											disabled={isReadOnly || isSubmitted}
										/>
										<FormControlLabel
											value={FreelancerProposalPricingType.Hourly}
											control={
												<Radio size="small" disabled={isReadOnly || isSubmitted} />
											}
											label={t("project.proposal.form.hourlyMode")}
											sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
											disabled={isReadOnly || isSubmitted}
										/>
									</RadioGroup>
								</FormControl>
							</Box>
						</Box>
					</Grid>

					{/* Tâches/Étapes - Affiché seulement après sélection du mode */}
					{showAdvancedSections && (
						<Grid size={12}>
							<Box sx={{ mb: 3 }}>
								<Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
									Estime ton temps et indique ton{" "}
									{pricingMode === FreelancerProposalPricingType.Daily ? "TJM" : "THM"}{" "}
									sur les étapes clés du projet.
								</Typography>

								{taskSteps.map(step => (
									<Card key={step.id} sx={{ mb: 2 }}>
										<CardContent>
											{taskSteps.length > 1 && !isReadOnly && !isSubmitted && (
												<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
													<IconButton
														onClick={() => removeTaskStep(step.id)}
														color="error"
														size="small"
													>
														<DeleteIcon />
													</IconButton>
												</Box>
											)}

											<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
												<TextField
													sx={{ flex: 3 }}
													label={t("project.proposal.form.taskDescription")}
													value={step.description}
													onChange={e =>
														updateTaskStep(step.id, "description", e.target.value)
													}
													disabled={isReadOnly || isSubmitted}
													slotProps={{
														input: { readOnly: isReadOnly }
													}}
												/>

												{pricingMode === FreelancerProposalPricingType.Daily ? (
													<>
														<TextField
															sx={{ flex: 0.75 }}
															type="number"
															label={t("project.proposal.form.numberOfDays")}
															value={step.days || ""}
															onChange={e =>
																updateTaskStep(
																	step.id,
																	"days",
																	parseFloat(e.target.value) || 0
																)
															}
															disabled={isReadOnly || isSubmitted}
															slotProps={{
																input: { readOnly: isReadOnly }
															}}
														/>
														<TextField
															sx={{ flex: 0.75 }}
															type="number"
															label={t("project.proposal.form.dailyRateEuro")}
															value={step.dailyRate || ""}
															onChange={e =>
																updateTaskStep(
																	step.id,
																	"dailyRate",
																	parseFloat(e.target.value) || 0
																)
															}
															disabled={isReadOnly || isSubmitted}
															slotProps={{
																input: { readOnly: isReadOnly }
															}}
														/>
													</>
												) : (
													<>
														<TextField
															sx={{ flex: 0.75 }}
															type="number"
															label={t("project.proposal.form.numberOfHours")}
															value={step.hours || ""}
															onChange={e =>
																updateTaskStep(
																	step.id,
																	"hours",
																	parseFloat(e.target.value) || 0
																)
															}
														/>
														<TextField
															sx={{ flex: 0.75 }}
															type="number"
															label={t("project.proposal.form.hourlyRateEuro")}
															value={step.hourlyRate || ""}
															onChange={e =>
																updateTaskStep(
																	step.id,
																	"hourlyRate",
																	parseFloat(e.target.value) || 0
																)
															}
														/>
													</>
												)}
											</Box>

											<Box sx={{ mt: 2 }}>
												<Typography
													variant="body2"
													sx={{ textAlign: "right", fontWeight: 500 }}
												>
													{t("project.proposal.form.subtotal")}:{" "}
													{pricingMode &&
													pricingMode === FreelancerProposalPricingType.Daily
														? (step.days || 0) * (step.dailyRate || 0)
														: (step.hours || 0) * (step.hourlyRate || 0)}{" "}
													€
												</Typography>
											</Box>
										</CardContent>
									</Card>
								))}

								{!isReadOnly && !isSubmitted && (
									<Button startIcon={<AddIcon />} onClick={addTaskStep} sx={{ mt: 1 }}>
										{t("project.proposal.form.addStep")}
									</Button>
								)}
							</Box>
						</Grid>
					)}

					{/* Budget Summary Section */}
					{showAdvancedSections && taskSteps.length > 0 && (
						<Grid size={12}>
							<Box sx={{ mb: 3 }}>
								<Card
									sx={{
										backgroundColor: zoullooColors.grey[25],
										border: `1px solid ${zoullooColors.grey[150]}`
									}}
								>
									<CardContent>
										<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
											{t("project.proposal.form.budgetSummary")}
										</Typography>

										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 1
											}}
										>
											<Typography variant="body1">
												{t("project.proposal.form.talentTimeBudget")}
											</Typography>
											<Typography variant="body1" sx={{ fontWeight: 500 }}>
												{calculateTasksTotal().toLocaleString("fr-FR", {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2
												})}{" "}
												€
											</Typography>
										</Box>

										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 1
											}}
										>
											<Typography variant="body1">
												{t("project.proposal.form.zoullooFees", {
													percentage: project?.feePercentage || 0
												})}
											</Typography>
											<Typography variant="body1" sx={{ fontWeight: 500 }}>
												{calculateZoullooFees().toLocaleString("fr-FR", {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2
												})}{" "}
												€
											</Typography>
										</Box>

										<Divider sx={{ my: 1 }} />

										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 1
											}}
										>
											<Typography variant="body1" sx={{ fontWeight: 600 }}>
												{t("project.proposal.form.totalTalentTimeBudget")}
											</Typography>
											<Typography
												variant="body1"
												sx={{ fontWeight: 600, fontSize: "1.1rem" }}
											>
												{calculateTotalWithFees().toLocaleString("fr-FR", {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2
												})}{" "}
												€
											</Typography>
										</Box>
									</CardContent>
								</Card>
							</Box>
						</Grid>
					)}

					{/* Coûts externes */}
					{showAdvancedSections && (
						<Grid size={12}>
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
									{t("project.proposal.form.externalCostsTitle")}
								</Typography>
								<Accordion
									expanded={externalCostsExpanded}
									onChange={(_, isExpanded) => setExternalCostsExpanded(isExpanded)}
									sx={{
										borderRadius: "8px !important",
										"&:before": { display: "none" },
										boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
									}}
								>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
											<Switch
												checked={hasExternalCosts}
												disabled={isReadOnly || isSubmitted}
												onChange={e => {
													setHasExternalCosts(e.target.checked);
													if (!e.target.checked) {
														// Save current data before hiding
														setSavedExternalCosts(externalCosts);
														setExternalCosts([]);
														setExternalCostsExpanded(false);
													} else {
														// Restore saved data when showing again, or add empty cost
														if (savedExternalCosts.length > 0) {
															setExternalCosts(savedExternalCosts);
														} else {
															// Always add an empty cost when activating
															setExternalCosts([
																{ id: `new_${Date.now()}`, description: "", amount: 0 }
															]);
														}
														// Auto-expand the accordion
														setExternalCostsExpanded(true);
													}
												}}
												onClick={e => e.stopPropagation()}
											/>
											<Typography sx={{ ml: 1 }}>
												{t("project.proposal.form.externalCosts")}
											</Typography>
										</Box>
									</AccordionSummary>

									{hasExternalCosts && (
										<AccordionDetails>
											{externalCosts.map(cost => (
												<Card key={cost.id} sx={{ mb: 2 }}>
													<CardContent>
														{!isSubmitted && (
															<Box
																sx={{
																	display: "flex",
																	justifyContent: "flex-end",
																	alignItems: "center",
																	mb: 2
																}}
															>
																<IconButton
																	onClick={() => removeExternalCost(cost.id)}
																	color="error"
																	size="small"
																>
																	<DeleteIcon />
																</IconButton>
															</Box>
														)}

														<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
															<TextField
																sx={{ flex: 3 }}
																label={t("project.proposal.form.externalCostDescription")}
																value={cost.description}
																onChange={e =>
																	updateExternalCost(
																		cost.id,
																		"description",
																		e.target.value
																	)
																}
																disabled={isReadOnly || isSubmitted}
															/>
															<TextField
																sx={{ flex: 0.75 }}
																type="number"
																label={t("project.proposal.form.externalCostBudget")}
																value={cost.amount || ""}
																onChange={e =>
																	updateExternalCost(
																		cost.id,
																		"amount",
																		parseFloat(e.target.value) || 0
																	)
																}
																disabled={isReadOnly || isSubmitted}
															/>
														</Box>
													</CardContent>
												</Card>
											))}

											{!isSubmitted && (
												<Button startIcon={<AddIcon />} onClick={addExternalCost}>
													{t("project.proposal.form.addExternalCost")}
												</Button>
											)}
										</AccordionDetails>
									)}
								</Accordion>
							</Box>
						</Grid>
					)}

					{/* Frais */}
					{showAdvancedSections && (
						<Grid size={12}>
							<Box sx={{ mb: 3 }}>
								<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
									{t("project.proposal.form.expensesTitle")}
								</Typography>
								<Accordion
									expanded={expensesExpanded}
									onChange={(_, isExpanded) => setExpensesExpanded(isExpanded)}
									sx={{
										borderRadius: "8px !important",
										"&:before": { display: "none" },
										boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
									}}
								>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
											<Switch
												checked={hasExpenses}
												disabled={isReadOnly || isSubmitted}
												onChange={e => {
													setHasExpenses(e.target.checked);
													if (!e.target.checked) {
														// Save current data before hiding
														setSavedExpenses(expenses);
														setExpenses([]);
														setExpensesExpanded(false);
													} else {
														// Restore saved data when showing again, or add empty expense
														if (savedExpenses.length > 0) {
															setExpenses(savedExpenses);
														} else {
															// Always add an empty expense when activating
															setExpenses([
																{
																	id: `new_${Date.now()}`,
																	nature: expenseNatures[0],
																	description: "",
																	amount: 0
																}
															]);
														}
														// Auto-expand the accordion
														setExpensesExpanded(true);
													}
												}}
												onClick={e => e.stopPropagation()}
											/>
											<Typography sx={{ ml: 1 }}>
												{t("project.proposal.form.expenses")}
											</Typography>
										</Box>
									</AccordionSummary>

									{hasExpenses && (
										<AccordionDetails>
											{expenses.map(expense => (
												<Card key={expense.id} sx={{ mb: 2 }}>
													<CardContent>
														{!isSubmitted && (
															<Box
																sx={{
																	display: "flex",
																	justifyContent: "flex-end",
																	alignItems: "center",
																	mb: 2
																}}
															>
																<IconButton
																	onClick={() => removeExpense(expense.id)}
																	color="error"
																	size="small"
																>
																	<DeleteIcon />
																</IconButton>
															</Box>
														)}

														<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
															<Autocomplete
																sx={{ flex: 1 }}
																options={expenseNatures}
																getOptionLabel={option => t(`enums.expenseNature.${option}`)}
																value={expense.nature}
																disableClearable
																onChange={(_, value) =>
																	updateExpense(expense.id, "nature", value || "")
																}
																disabled={isReadOnly || isSubmitted}
																renderInput={params => (
																	<TextField
																		{...params}
																		label={t("project.proposal.form.expenseNature")}
																	/>
																)}
															/>
															<TextField
																sx={{ flex: 2 }}
																label={t("project.proposal.form.expenseDescription")}
																value={expense.description}
																onChange={e =>
																	updateExpense(expense.id, "description", e.target.value)
																}
																disabled={isReadOnly || isSubmitted}
															/>
															<TextField
																sx={{ flex: 0.75 }}
																type="number"
																label={t("project.proposal.form.expenseBudget")}
																value={expense.amount || ""}
																onChange={e =>
																	updateExpense(
																		expense.id,
																		"amount",
																		parseFloat(e.target.value) || 0
																	)
																}
																disabled={isReadOnly || isSubmitted}
															/>
														</Box>
													</CardContent>
												</Card>
											))}

											{!isSubmitted && (
												<Button startIcon={<AddIcon />} onClick={addExpense}>
													{t("project.proposal.form.addExpense")}
												</Button>
											)}
										</AccordionDetails>
									)}
								</Accordion>
							</Box>
						</Grid>
					)}

					{/* Documents */}
					{showAdvancedSections && (
						<Grid size={12}>
							<Box sx={{ mb: 3 }}>
								<DocumentTable
									documents={documents}
									onAddNewDocument={isSubmitted ? undefined : handleAddNewDocument}
									onFileUpload={isSubmitted ? undefined : handleFileUpload}
									onConfirmAddDocument={
										isSubmitted ? undefined : handleConfirmAddDocument
									}
									onCancelAddDocument={isSubmitted ? undefined : handleCancelAddDocument}
									onRemoveDocument={isSubmitted ? undefined : handleRemoveDocument}
									currentUserId={user.id}
									getDocumentUrl={getDocumentUrl}
									isAddingDocument={isAddingDocument}
									addingDocument={addingDocument}
									onDocumentChange={isSubmitted ? undefined : handleDocumentChange}
									showTypeColumn={false}
									fullWidth={true}
									displayMode={isSubmitted}
								/>
							</Box>
						</Grid>
					)}

					{/* Comments Section */}
					{showAdvancedSections && (
						<Grid size={12}>
							<Box sx={{ mb: 3 }}>
								<Typography variant="body1" sx={{ fontWeight: 500, mb: 3 }}>
									{t("project.proposal.form.comments")}
								</Typography>
								<HtmlEditor
									content={comments || ""}
									onUpdate={({ editor }) => {
										const newValue = editor.getHTML();
										setComments(newValue);
									}}
									placeholder={t("project.proposal.form.commentsPlaceholder")}
									readonly={isReadOnly || isCommentsDisabled}
									sx={{
										"& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror":
											{
												height: "150px !important"
											}
									}}
								/>
							</Box>
						</Grid>
					)}

					{/* Grand Total Section */}
					{showAdvancedSections && taskSteps.length > 0 && (
						<Grid size={12}>
							<Card
								sx={{
									backgroundColor: zoullooColors.secondary.light + "20",
									border: `2px solid ${zoullooColors.secondary.main}`
								}}
							>
								<CardContent>
									<Typography
										variant="h6"
										sx={{ mb: 2, fontWeight: 600, color: zoullooColors.secondary.main }}
									>
										{t("project.proposal.form.grandTotal")}
									</Typography>

									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1
										}}
									>
										<Typography variant="body1">Budget Total Temps Talent</Typography>
										<Typography variant="body1" sx={{ fontWeight: 500 }}>
											{calculateTotalWithFees().toLocaleString("fr-FR", {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2
											})}{" "}
											€
										</Typography>
									</Box>

									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1
										}}
									>
										<Typography variant="body1">
											{t("project.proposal.form.externalCostsLabel")}
										</Typography>
										<Typography variant="body1" sx={{ fontWeight: 500 }}>
											{(hasExternalCosts && externalCosts.length > 0
												? calculateExternalCostsTotal()
												: 0
											).toLocaleString("fr-FR", {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2
											})}{" "}
											€
										</Typography>
									</Box>

									{/* External Costs Management Fees */}
									{project?.externalCostsFeePercentage &&
										project.externalCostsFeePercentage > 0 &&
										hasExternalCosts &&
										externalCosts.length > 0 && (
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													mb: 1
												}}
											>
												<Typography
													variant="body1"
													sx={{ pl: 2, color: "text.secondary" }}
												>
													{t("project.externalCostsFeePercentage")} (
													{project.externalCostsFeePercentage}%)
												</Typography>
												<Typography variant="body1" sx={{ fontWeight: 500 }}>
													{(
														(calculateExternalCostsTotal() *
															project.externalCostsFeePercentage) /
														100
													).toLocaleString("fr-FR", {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2
													})}{" "}
													€
												</Typography>
											</Box>
										)}

									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 1
										}}
									>
										<Typography variant="body1">
											{t("project.proposal.form.expensesLabel")}
										</Typography>
										<Typography variant="body1" sx={{ fontWeight: 500 }}>
											{(hasExpenses && expenses.length > 0
												? calculateExpensesTotal()
												: 0
											).toLocaleString("fr-FR", {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2
											})}{" "}
											€
										</Typography>
									</Box>

									{/* Expenses Management Fees */}
									{project?.expensesFeePercentage &&
										project.expensesFeePercentage > 0 &&
										hasExpenses &&
										expenses.length > 0 && (
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													mb: 1
												}}
											>
												<Typography
													variant="body1"
													sx={{ pl: 2, color: "text.secondary" }}
												>
													{t("project.expensesFeePercentage")} (
													{project.expensesFeePercentage}%)
												</Typography>
												<Typography variant="body1" sx={{ fontWeight: 500 }}>
													{(
														(calculateExpensesTotal() * project.expensesFeePercentage) /
														100
													).toLocaleString("fr-FR", {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2
													})}{" "}
													€
												</Typography>
											</Box>
										)}

									{/* Processing Fees */}
									{project?.processingFees && project.processingFees > 0 && (
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 1
											}}
										>
											<Typography variant="body1">
												{t("project.processingFees")}
											</Typography>
											<Typography variant="body1" sx={{ fontWeight: 500 }}>
												{project.processingFees.toLocaleString("fr-FR", {
													minimumFractionDigits: 2,
													maximumFractionDigits: 2
												})}{" "}
												€
											</Typography>
										</Box>
									)}

									<Divider sx={{ my: 2 }} />

									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center"
										}}
									>
										<Typography
											variant="h6"
											sx={{ fontWeight: 700, color: zoullooColors.secondary.main }}
										>
											{t("project.proposal.form.totalBudgetWithExtras")}
										</Typography>
										<Typography
											variant="h6"
											sx={{
												fontWeight: 700,
												fontSize: "1.3rem",
												color: zoullooColors.secondary.main
											}}
										>
											{calculateGrandTotal().toLocaleString("fr-FR", {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2
											})}{" "}
											€
										</Typography>
									</Box>
								</CardContent>
							</Card>
						</Grid>
					)}

					{/* Actions */}
					<Grid size={12}>
						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
							{/* Only show Cancel button when not in workflow mode */}
							{!isWorkflowMode && (
								<Button
									variant="outlined"
									color="secondary"
									onClick={onCancel}
									disabled={isSubmitting || isDraftSaving}
								>
									{isReadOnly ? t("common.close", "Fermer") : t("common.cancel")}
								</Button>
							)}
							{/* Show Save Draft and Submit buttons only if not read-only and not in workflow mode */}
							{!isReadOnly && !isWorkflowMode && (
								<>
									{/* Show Save Draft button only if proposal is not submitted */}
									{!isSubmitted && (
										<Button
											variant="outlined"
											color="secondary"
											onClick={handleSaveDraft}
											disabled={isSubmitting || isDraftSaving}
											startIcon={
												isDraftSaving ? <CircularProgress size={16} /> : undefined
											}
										>
											{isDraftSaving
												? t("project.proposal.form.saving")
												: t("project.proposal.form.saveDraft")}
										</Button>
									)}
									<Button
										variant="contained"
										color="secondary"
										onClick={handleSubmitProposal}
										disabled={isSubmitting || isDraftSaving}
										startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
									>
										{(() => {
											if (isSubmitting) return t("project.proposal.form.updating");
											return existingProposal?.status ===
												FreelancerProposalStatus.SubmittedCSM
												? t("project.proposal.form.updateProposal")
												: t("project.proposal.form.submitProposal");
										})()}
									</Button>
								</>
							)}
						</Box>
					</Grid>
				</Grid>

				{/* Snackbar for feedback */}
				<Snackbar
					open={snackbar.open}
					autoHideDuration={6000}
					onClose={handleCloseSnackbar}
					anchorOrigin={{ vertical: "top", horizontal: "center" }}
				>
					<Alert
						onClose={handleCloseSnackbar}
						severity={snackbar.severity}
						sx={{ width: "100%" }}
					>
						{snackbar.message}
					</Alert>
				</Snackbar>
			</Box>
		);
	}
);

ProposalForm.displayName = "ProposalForm";

export default ProposalForm;
